import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Modal, Notification, StackView, Spacer, TextInput, Typography } from '@telus-uds/components-web';
import request from '../utils/request';
// import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { useLocaleContext } from '../context/LocaleContext';
import { validateCoupon as validateCouponUtil } from '../utils/couponValidation';

const CouponPopup = ({ 
  isOpen, 
  onClose, 
  onCouponApplied,
  formFields,
  orderData,
  currentSite = 1,
  isMultisiteOrder = false
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [validatedCoupon, setValidatedCoupon] = useState(null);
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';

  // Helper function to get locale-specific value from bilingual string
  const getLocaleValue = (bilingualString) => {
    if (!bilingualString) return '';
    const str = String(bilingualString);
    const arr = str.split(', ');
    let out = [];
    for (let i = 0; i < arr.length; i += 2) {
      if (arr[i] && arr[i + 1]) {
        out.push(isFrench ? arr[i + 1] : arr[i]);
      }
    }
    return out.join(', ');
  };

  // Reset state when popup opens/closes
  useEffect(() => {
    if (isOpen) {
      setCouponCode('');
      setValidationError('');
      setValidatedCoupon(null);
    }
  }, [isOpen]);

  const validateCoupon = async () => {
    const normalizedCouponCode = couponCode.trim();
    
    if (!normalizedCouponCode) {
      setValidationError('Please enter a coupon code');
      return;
    }

    setIsValidating(true);
    setValidationError('');

    try {
      // Get all available coupons
      const response = await request.get('/orderCoupons');
      let coupons = response.data;
      
      // Handle different possible response structures
      const { data, coupons: couponsList, results } = coupons;
      if (data && Array.isArray(data)) {
        coupons = data;
      } else if (couponsList && Array.isArray(couponsList)) {
        coupons = couponsList;
      } else if (results && Array.isArray(results)) {
        coupons = results;
      }

      if (!Array.isArray(coupons)) {
        setValidationError('No coupons available');
        return;
      }

      // Use the utility function for validation (with normalized coupon code)
      const validationResult = await validateCouponUtil(normalizedCouponCode, coupons, formFields, orderData, isFrench, { currentSite, isMultisiteOrder });

      if (!validationResult.isValid) {
        setValidationError(validationResult.error);
        return;
      }

      // Coupon is valid
      setValidatedCoupon(validationResult.coupon);
      setValidationError('');

    } catch (error) {
      setValidationError('Error validating coupon. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const applyCoupon = () => {
    if (validatedCoupon) {
      onCouponApplied(validatedCoupon);
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isValidating) {
      validateCoupon();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant={{ width: 'xl' }}
    >
      <div style={{ width: "100%", padding: "1rem 0", maxHeight: "70vh", overflowY: "auto" }}>
        <StackView space={4}>
          <div>
            <TextInput
              label={isFrench ? 'Code de Coupon' : 'Coupon Code'}
              value={couponCode}
              onChange={(value) => setCouponCode(value)}
              onKeyPress={handleKeyPress}
              placeholder={isFrench ? 'Entrez le code de coupon' : 'Enter coupon code'}
              disabled={isValidating}
            />
          </div>

          {validationError && (
            <Notification variant={{ validation: 'error' }}>
              <Typography>{validationError}</Typography>
            </Notification>
          )}

          {validatedCoupon && (
            <StackView space={3}>
              <Divider />
              <div>
                <Typography heading="h4" variant={{ size: 'h4' }} block>
                  {isFrench ? 'Détails du Coupon' : 'Coupon Details'}
                </Typography>
                <div style={{ marginTop: '16px' }}>
                  <div><strong>{isFrench ? 'Code:' : 'Code:'}</strong> {(validatedCoupon.coupon && validatedCoupon.coupon.couponCode) || validatedCoupon.couponCode}</div>
                  <div><strong>{isFrench ? 'Type:' : 'Type:'}</strong> {getLocaleValue((validatedCoupon.coupon && validatedCoupon.coupon.couponType) || validatedCoupon.couponType) || 'General'}</div>
                  <div><strong>{isFrench ? 'Valeur:' : 'Value:'}</strong> {(() => {
                    const value = (validatedCoupon.coupon && validatedCoupon.coupon.dollarValue) || validatedCoupon.dollarValue;
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    if (isNaN(numValue)) return `$${value || '0.00'}`;
                    return numValue < 0 ? `-$${Math.abs(numValue)}` : `$${numValue}`;
                  })()}</div>
                  <div><strong>{isFrench ? 'Durée:' : 'Duration:'}</strong> {getLocaleValue((validatedCoupon.coupon && validatedCoupon.coupon.duration) || validatedCoupon.duration)}</div>
                  {validatedCoupon.coupon && validatedCoupon.coupon.descInstallEn && (
                    <div>
                      <strong>{isFrench ? 'Description:' : 'Description:'}</strong>{' '}
                      {isFrench 
                        ? (validatedCoupon.coupon.descInstallFr || validatedCoupon.coupon.descInstallEn)
                        : validatedCoupon.coupon.descInstallEn
                      }
                    </div>
                  )}
                </div>
              </div>
            </StackView>
          )}

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button
              type="button"
              onPress={() => {
                if (!isValidating && !validatedCoupon) {
                  validateCoupon();
                }
              }}
              disabled={!!validatedCoupon}
              variant={{ priority: 'high' }}
            >
              {(() => {
                if (isValidating) {
                  return isFrench ? 'Validation...' : 'Validating...';
                }
                return isFrench ? 'Valider' : 'Validate';
              })()}
            </Button>
            
            <Button
              type="button"
              onPress={applyCoupon}
              disabled={!validatedCoupon}
              variant={{ priority: 'high' }}
            >
              {isFrench ? 'Appliquer' : 'Apply Coupon'}
            </Button>
          </div>
        </StackView>
      </div>
    </Modal>
  );
};

CouponPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCouponApplied: PropTypes.func.isRequired,
  formFields: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired,
  currentSite: PropTypes.number,
  isMultisiteOrder: PropTypes.bool
};

export default CouponPopup;
